/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { I18nService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { ToolService } from 'app/common/services';

import { ClientType } from './nfs-share.enum';

@Injectable()
export class NfsShareStrService {
    i18n = {
        btn: {
            createAsBtn: this.get('common.createSchema_btn'),
            createBtn: this.get('common.create_button'),
            deleteBtn: this.get('common.delete_button'),
            modifyBtn: this.get('common.modify_button'),
            okBtn: this.get('common.confirm_button'),
            cancelBtn: this.get('common.cancel_button'),
            closeBtn: this.get('common.close_button'),
            actionBtn: this.get('common.operation_button'),
            addBtn: this.get('common.add_button'),
            removeBtn: this.get('common.remove_button'),
            addClient: this.get('service.share_term_addClient_button'),
            addExtensionFilteringBtn: this.get('service.share_task_filenameextensionfiltering_add_title'),
        },
        label: {
            // 共享名称
            share_name: this.get('service.share_term_shareName_lab'),
            //  名称
            name: this.get('common.name_column'),
            //  多个客户端
            clients: this.get('service.share_term_addClient_clients_lab'),
            //  单个客户端
            client: this.get('service.share_term_addClient_client_lab'),
            // 描述
            description: this.get('common.description_column'),
            // 文件系统
            fileSystem: this.get('service.quota_term_create_FS_lab'),
            // 所属租户
            vStoreLab: this.get('common.owningVstore_lab'),
            // dtree
            dtree: this.get('service.quota_term_create_Dtree_lab'),
            // 共享路径
            share_path: this.get('service.share_term_sharePath_lab'),
            // 所属存储池
            storagePool: this.get('storage.LunGroup_term_belongedStoragePool_label'),
            // ID
            id: this.get('common.ID_column'),
            // 类型
            type: this.get('common.type_column'),
            // 权限级别
            permissionLevel: this.get('service.share_term_addClient_permissionLevel_lab'),
            // UNIX权限
            unixPermissionLab: this.get('service.share_term_addClient_UNIXPermission_lab'),
            // Kerberos5权限
            kerberos5PermissionLab: this.get('service.share_term_addClient_Kerberos5Permission_lab'),
            // Kerberos5i权限
            kerberos5iPermissionLab: this.get('service.share_term_addClient_Kerberos5iPermission_lab'),
            // Kerberos5p权限
            kerberos5pPermissionLab: this.get('service.share_term_addClient_Kerberos5pPermission_lab'),
            // 高级
            advance: this.get('common.advance_label'),
            // 高级设置 [极简模式下]
            advancedSettingTitle: this.get('service.share_simplifiedMode_create_advance_btn'),
            // 写入模式
            sync: this.get('service.share_term_addClient_writeMode_lab'),
            // 权限限制
            all_squash: this.get('service.share_term_addClient_permissionLimit_lab'),
            // root权限限制
            root_squash: this.get('service.share_term_addClient_rootPermissionLimit_lab'),
            // 源端口校验限制
            secure: this.get('service.share_term_addClient_sourcePortVerification_lab'),
            // 创建模式
            createStyle: this.get('service.share_term_createFrom_lab'),
            // copy from
            copyFrom: this.get('service.share_term_createMode_copyfrom_value'),
            // new
            new: this.get('common.newCreate_checkbox'),
            // 共享
            share: this.get('common.EnumDefine_MOTYPE_SHARE'),
            // 共享s
            shares: this.get('common.share_title'),
            // 逗号
            comma: this.get('common.comma_label'),
            // 启用
            enable: this.get('common.enable_label'),
            // 已启用
            enabled: this.get('common.enabled_switch_column'),
            // 未启用
            disable: this.get('common.disabled_switch_column'),
            // 字符编码
            charEncoding: this.get('service.share_term_charEncoding_lab'),
            // 显示Snapshot
            showSnapShotLab: this.get('service.share_term_createCIFS_showSnapshot_lab'),
            // 显示SnapshotTip
            showSnapShotTip: this.get('service.share_term_createCIFS_showSnapshot_tip'),
            auditFs: this.get('service.share_term_selectAuditFS_info'),
            fileNameExtensionFilter: this.get('service.share_task_filenameextensionfiltering_label'),
            fileNameExtension: this.get('file name extension'),
            ruleType: this.get('service.share_task_filenameextensionfilteringRule_lab'),
            anonymousUserId: this.get('service.share_term_addClient_anonymousID_label'),
            nfsCharCodeTip: this.get('service.share_term_charEncoding_NFSv4_tip'),
        },
        title: {
            // NFS
            nfs: this.get('common.NFS_lab'),
            // 模板创建nfs
            createAsTitle: this.get('service.share_term_createNFSfromTemp_title'),
            // 创建nfs
            createTitle: this.get('service.share_term_createNFS_title'),
            // 基本信息
            basicInfo: this.get('common.basicInfo_title'),
            // 选择文件系统
            selectFS: this.get('service.share_term_create_chooseFilesystem_title'),
            // 选择Dtree
            selectDtree: this.get('service.share_term_create_chooseDtree_title'),
            // 删除NFS共享
            deleteShare: this.get('service.share_term_deleteNFS_title'),
            // 修改NFS共享
            modifyShare: this.get('service.share_term_modifyNFS_title'),
            // 权限列表
            permissionList: this.get('service.share_term_view_permissions_lab'),
            // 添加客户端
            addClient: this.get('service.share_term_addClient_button'),
            // 修改客户端
            modifyClient: this.get('service.share_term_modifyClient_title'),
            // 移除客户端
            removeClient: this.get('service.share_term_removeClient_tittle'),
            extensionFiltering: this.get('service.share_task_filenameextensionfilteringRule_tab'),
            /**
             * 增加NFS共享权限
             */
            addNfsPermission: this.get('service.share_term_createNFS_addPermission_title'),
        },
        tip: {
            // 请先选择所属租户。
            plsSelectVstore: this.get('service.share_term_create_chooseVstore_tip'),
            // 请先选择copyfrom的共享。
            plsSlectShare: this.get('service.share_term_create_modify_err_tip'),
            // 权限数{0}。
            permisssions: (num: string): string => this.get('service.share_term_create_permissionNum_lab', [num]),
            // 权限数{0},{1}
            permissionsWithAllClient: (num: number, str: string): string =>
                this.get('service.share_term_create_permissionNum_value_lab', [num, str]),
            /**
             * 所有客户端可读写
             */
            allClient: this.get('service.share_term_create_permission_allClinetRW_value'),
            /**
             * 所有客户端只读
             */
            allClientOnlyRead: this.get('service.share_term_create_permission_allClinetRead_value'),
            /**
             * 审计日志文件系统不支持模板创建共享
             */
            copyFromDisabledTip: this.get('审计日志文件系统不支持模板创建共享'),
            // 添加客户端主机输入说明
            addClientTip1: this.get('service.share_task_createnfs_addClient_hostNameorIPaddress_tip'),
            // 添加客户端网络组输入说明
            addClientTip2: this.get('service.share_task_createnfs_addClient_netGroupNameorIPaddress_tip'),
            // 只有加入权限列表的客户端才能访问
            clientsTip: this.get('service.share_term_view_permissions_tip'),
            /**
             * 当前NFS共享服务
             */
            NFSServiceOffTip1: this.get('service.share_term_enableNFS_info_lab'),
            /**
             * 开始NFS共享服务
             */
            NFSServiceOffTip2: this.get('service.share_term_enableNFS_info_lab1'),
            /**
             * 。
             */
            NFSServiceOffTip3: this.get('service.share_term_enableNFS_info_lab2'),
            all_squash: this.get('service.share_term_addClient_permissionLimit_meaning_tip'),
            root_squash: this.get('service.share_term_addClient_rootPermissionLimit_meaning_tip'),
            secure: this.get('service.share_term_addClient_sourcePortVerification_meaning_tip'),
            sync: this.get('service.share_createnfs_task_addClient_writeMode_tip'),
            type: this.get('service.share_createnfs_task_addClient_type_tip'),
            /**
             * 已选审计日志文件系统，权限列表被清空。
             */
            permissionClearTip: this.get('service.share_term_clearPermissions_msg'),
            // 如果配置了DNS服务器，请在DNS服务器上添加客户端IP所在网段的反向解析目录，否则可能会导致主机IO时延上升
            dnsConfigureTip: this.get('service.share_term_addClient_networkGroup_configDNS_info'),
            fileNameExtensionFilter: this.get('service.share_task_filenameextensionfiltering_description'),
            gnsCreateNfsTip: this.get('service.share_term_createNFS_err_tip'),
            anonymousUserId: this.get('service.share_term_addClient_anonymousID_tip'),
        },
        error: {
            // copy from 的共享不能为空
            copyFromRequired: this.get('service.share_term_createMode_copyfrom_valid'),
            /**
             * 主机格式要求如下。。。
             */
            hostName: this.get('service.share_term_addClient_nameorIPaddress_valid', [1, 255]),
            /**
             * 网络组名称不符合要求
             */
            netName: this.get('service.share_term_addClient_type_networkGroup_name_validation'),
            smartRecommendDataTimeoutMsg: this.get('rest.getSmartRecommendDataTimeout_msg'),
            shareName: this.get('service.share_term_createNFS_name_valid'),
        },
        highRisk: {
            deleteNFS: this.get('service.share_deletenfs_task_removeShare_confirmation_message'),
            removeClient: this.get('service.share_deletenfs_task_removeClient_confirmation_message'),
            modifyNFS: this.get('service.share_modifynfs_task_modifyShare_confirmation_message'),
            modifyClient: this.get('service.share_modifynfs_task_modifyClient_confirmation_message'),
            modifyCodeingNFS: this.get('service.share_createnfs_task_modifyCharEncoding_info_msg'),
            modifyAnonymousId: this.get('service.share_term_addClient_modifyAnonymousID_msg'),
        },
        rest: {
            // 创建NFS共享{0}
            createNFS: this.get('rest.CreateNfsShare_msg'),
            // 修改NFS共享{0}
            modifyShare: this.get('rest.ModifyNfsShare_msg'),
            // 删除NFS共享{0}
            deleteNFS: this.get('rest.DeleteNfsShare_msg'),
            // 增加NFS共享客户端（{0}）{1}
            addClients: this.get('rest.AddClient_msg'),
            // 修改NFS共享客户端（{0}）{1}
            modifyClient: this.get('rest.ModifyClient_msg'),
            // 删除NFS共享客户端（{0}）{1}
            removeClients: this.get('rest.DeleteClient_msg'),
            /**
             * 创建增加客户端任务
             */
            addTaskAddClient: this.get('rest.createTask_addClient_msg'),
        },
        value: {
            host: this.get('service.share_term_addClient_host_lab'),
            net: this.get('service.share_term_addClient_netGroup_lab'),
        },
    };

    /**
     * 客户端类型，
     *
     * 主机 host
     *
     * 网络组 net
     */
    clientTypeOpts: SelectOption[] = [
        {
            label: this.i18nService.get('common.host_title'),
            value: ClientType.host,
        },
        {
            label: this.i18nService.get('service.share_term_addClient_type_networkGroup_checkbox'),
            value: ClientType.net,
        },
    ];

    /**
     * 权限级别，
     *
     * 只读 0
     *
     * 读写 1
     */
    permissionLevelOpts: SelectOption[] = [
        {
            label: this.i18nService.get('settings.userAndRole_term_readonly_list'),
            value: '0',
        },
        {
            label: this.i18nService.get('protection.hyperMetroCG_view_localResourceHostStatus_readAndWrite_value'),
            value: '1',
        },
        {
            label: this.i18nService.get('common.term_confirm_none'),
            value: '5',
        },
    ];

    /**
     * 权限级别(升级日志文件系统创NFS共享客户端只允许只读权限)
     *
     * 只读 0
     */
    auditLogFsPermissionLevelOpts: SelectOption[] = [
        {
            label: this.i18nService.get('settings.userAndRole_term_readonly_list'),
            value: '0',
        },
        {
            label: this.i18nService.get('common.term_confirm_none'),
            value: '5',
        },
    ];

    /**
     * 写入模式，
     *
     * 同步 0
     *
     * 异步 1
     */
    syncOpts: SelectOption[] = [
        {
            label: this.i18nService.get('service.share_term_addClient_writeMode_sync_lab'),
            value: '0',
        },
        {
            label: this.i18nService.get('service.share_term_addClient_writeMode_async_lab'),
            value: '1',
        },
    ];

    /**
     * 权限限制，
     *
     * all_squash 0
     *
     * no_all_squash 1
     */
    all_squashOpts: SelectOption[] = [
        {
            label: this.i18nService.get('service.share_term_addClient_permissionLimit_option_allsquash_radio'),
            value: '0',
        },
        {
            label: this.i18nService.get('service.share_term_addClient_permissionLimit_option_noallsquash_radio'),
            value: '1',
        },
    ];

    /**
     * root权限限制，
     *
     * root_squash 0
     *
     * no_root_squash 1
     */
    root_squashOpts: SelectOption[] = [
        {
            label: this.i18nService.get('service.share_term_addClient_rootPermissionLimit_option_rootsquash_radio'),
            value: '0',
        },
        {
            label: this.i18nService.get('service.share_term_addClient_rootPermissionLimit_option_norootsquash_radio'),
            value: '1',
        },
    ];

    /**
     * 源端口校验限制
     *
     * secure 0
     *
     * insecure 1
     */
    secureOpts: SelectOption[] = [
        {
            label: this.i18nService.get('service.share_term_addClient_sourcePortVerification_label_secure_radio'),
            value: '0',
        },
        {
            label: this.i18nService.get('service.share_term_addClient_sourcePortVerification_label_insecure_radio'),
            value: '1',
        },
    ];

    /**
     * 613与二级存储原有的字符编码
     */
    charEncodingTypeObj = {
        0: this.i18nService.get('service.share_term_charEncoding_0'),
        11: this.i18nService.get('service.share_term_charEncoding_11'),
        12: this.i18nService.get('service.share_term_charEncoding_12'),
        13: this.i18nService.get('service.share_term_charEncoding_13'),
        14: this.i18nService.get('service.share_term_charEncoding_14'),
        21: this.i18nService.get('service.share_term_charEncoding_21'),
        22: this.i18nService.get('service.share_term_charEncoding_22'),
        23: this.i18nService.get('service.share_term_charEncoding_23'),
        30: this.i18nService.get('service.share_term_charEncoding_30'),
        31: this.i18nService.get('service.share_term_charEncoding_31'),
        32: this.i18nService.get('service.share_term_charEncoding_32'),
        33: this.i18nService.get('service.share_term_charEncoding_33'),
        34: this.i18nService.get('service.share_term_charEncoding_34'),
        40: this.i18nService.get('service.share_term_charEncoding_40'),
    };

    /**
     * 615新增的字符编码
     */
    charEncodingTypeExpandObj = {
        41: this.i18nService.get('service.share_term_charEncoding_41'),
        42: this.i18nService.get('service.share_term_charEncoding_42'),
        43: this.i18nService.get('service.share_term_charEncoding_43'),
        44: this.i18nService.get('service.share_term_charEncoding_44'),
        45: this.i18nService.get('service.share_term_charEncoding_45'),
        46: this.i18nService.get('service.share_term_charEncoding_46'),
        47: this.i18nService.get('service.share_term_charEncoding_47'),
        48: this.i18nService.get('service.share_term_charEncoding_48'),
        49: this.i18nService.get('service.share_term_charEncoding_49'),
        50: this.i18nService.get('service.share_term_charEncoding_50'),
        51: this.i18nService.get('service.share_term_charEncoding_51'),
        52: this.i18nService.get('service.share_term_charEncoding_52'),
        53: this.i18nService.get('service.share_term_charEncoding_53'),
        54: this.i18nService.get('service.share_term_charEncoding_54'),
        55: this.i18nService.get('service.share_term_charEncoding_55'),
        56: this.i18nService.get('service.share_term_charEncoding_56'),
        60: this.i18nService.get('service.share_term_charEncoding_60'),
        61: this.i18nService.get('service.share_term_charEncoding_61'),
    };

    charEncodingType = { ...this.charEncodingTypeObj, ...this.charEncodingTypeExpandObj };

    /**
     * 编码类型
     */
    charEncodingOpts: SelectOption[] = Object.keys(this.charEncodingType).map((key) => (
        {
            label: this.charEncodingType[key],
            value: key,
        }
    ));
    /**
     * 显示Snapshot
     *
     */
    enableShowSnapOpts: SelectOption[] = [
        {
            label: this.i18nService.get('common.enabled_switch_column'),
            value: true,
        },
        {
            label: this.i18nService.get('common.disabled_switch_column'),
            value: false,
        },
    ];

    constructor(
        private i18nService: I18nService,
        private toolService: ToolService,
    ) {
    }

    get(key, params?, colon?): string {
        return this.i18nService.get(key, params, colon);
    }

    requiredErr(name: string): string {
        return this.get('common.null_validation', [name]);
    }

    rangeByteLengthErr(min: number, max: number): string {
        return this.get('common.length_msg', [min, max]);
    }

    rangeByteOneLengthErr(min: number, max: number): string {
        return this.get('common.length_oneValue_tip', [min, max]);
    }

    commonNameErr(name: string): string {
        return this.get('common.name_vadlitaion', [name]);
    }

    deleteResult(name: string): string {
        return this.get('rest.delete_result_tip', [name]);
    }

    repeatErr(name: string): string {
        return this.get('common.existence_validation', [name]);
    }
}
